import { useEffect, useRef, useState } from 'react';
import './styles/admin.scss';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import { useConfirm } from '../../components/admin/useConfirm';
import { ATTEMPT_MODE, SUBJECT_LABEL } from './adminConstants';

type Attempt = { id: string; userEmail: string; mode: number; subject?: number | null; scorePercent: number; correctQuestions: number; totalQuestions: number; createdAt: string };
type Chat = { id: string; userEmail: string; role: number; content: string; createdAt: string };

const fmt = (d: string) => { const x = new Date(d); return x.toLocaleString("cs-CZ", { day: "numeric", month: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }); };
const PAGE = 30;

export default function AdminLogs() {
    const [tab, setTab] = useState<"pokusy" | "chat">("pokusy");
    const [attempts, setAttempts] = useState<Attempt[]>([]);
    const [chats, setChats] = useState<Chat[]>([]);
    const [total, setTotal] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [search, setSearch] = useState("");
    const [skip, setSkip] = useState(0);
    const [refresh, setRefresh] = useState(0);
    const reqId = useRef(0);
    const api = new ApiHelper();
    const { ask, confirmModal } = useConfirm();

    // single source of truth: one debounced, race-guarded load keyed on tab/skip/search/refresh
    useEffect(() => {
        const id = ++reqId.current;
        const t = setTimeout(async () => {
            try {
                if (tab === "pokusy") {
                    const d = await (await api.adminLogsApi.attempts({ search: search.trim() || undefined, skip, take: PAGE })).json();
                    if (id === reqId.current) { setAttempts(d.rows); setTotal(d.total); }
                } else {
                    const d = await (await api.adminLogsApi.chat({ search: search.trim() || undefined, skip, take: PAGE })).json();
                    if (id === reqId.current) { setChats(d.rows); setTotal(d.total); }
                }
            } catch (x: any) { if (id === reqId.current) api.adminError(x); }
            finally { if (id === reqId.current) setLoaded(true); }
        }, search ? 300 : 0);
        return () => clearTimeout(t);
    }, [tab, skip, search, refresh]);

    function changeTab(t: "pokusy" | "chat") { if (t === tab) return; setLoaded(false); setSkip(0); setTab(t); }
    function delAttempt(a: Attempt) { ask(<>Smazat tento pokus studenta {a.userEmail}?</>, async () => { try { await api.adminLogsApi.deleteAttempt(a.id); setRefresh(r => r + 1); } catch (x: any) { api.adminError(x); } }); }
    function delChat(c: Chat) { ask(<>Smazat tuto zprávu studenta {c.userEmail}?</>, async () => { try { await api.adminLogsApi.deleteChat(c.id); setRefresh(r => r + 1); } catch (x: any) { api.adminError(x); } }); }

    return (
        <div>
            <div className="adminHead"><div><h1>Logy</h1><p>Pokusy v testech a konverzace s AI tutorem (jen čtení + mazání)</p></div></div>
            <div className="subTabs">
                <span className={"subTab" + (tab === "pokusy" ? " active" : "")} onClick={() => changeTab("pokusy")}>Pokusy v testech</span>
                <span className={"subTab" + (tab === "chat" ? " active" : "")} onClick={() => changeTab("chat")}>AI tutor</span>
            </div>

            <div className="searchBar"><input placeholder="Hledat podle e-mailu…" value={search} onChange={e => { setSkip(0); setSearch(e.target.value); }} /></div>

            {tab === "pokusy" ?
                <table className="aTable">
                    <thead><tr><th>Student</th><th>Typ</th><th>Předmět</th><th>Skóre</th><th>Kdy</th><th></th></tr></thead>
                    <tbody>
                        {attempts.map(a => (
                            <tr key={a.id}>
                                <td>{a.userEmail}</td><td>{ATTEMPT_MODE[a.mode]}</td><td>{a.subject != null ? SUBJECT_LABEL[a.subject] : <span className="muted">—</span>}</td>
                                <td>{a.correctQuestions}/{a.totalQuestions} ({a.scorePercent} %)</td><td>{fmt(a.createdAt)}</td>
                                <td><span className="actBtn del" onClick={() => delAttempt(a)}>×</span></td>
                            </tr>
                        ))}
                        {loaded && attempts.length === 0 && <tr><td colSpan={6} className="muted">Žádné pokusy.</td></tr>}
                    </tbody>
                </table>
                :
                <table className="aTable">
                    <thead><tr><th>Student</th><th>Role</th><th>Zpráva</th><th>Kdy</th><th></th></tr></thead>
                    <tbody>
                        {chats.map(c => (
                            <tr key={c.id}>
                                <td>{c.userEmail}</td><td>{c.role === 0 ? "Student" : "Tutor"}</td>
                                <td><span className="clamp2">{c.content}</span></td><td>{fmt(c.createdAt)}</td>
                                <td><span className="actBtn del" onClick={() => delChat(c)}>×</span></td>
                            </tr>
                        ))}
                        {loaded && chats.length === 0 && <tr><td colSpan={5} className="muted">Žádné zprávy.</td></tr>}
                    </tbody>
                </table>
            }

            <div className="aPager">
                <button disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE))}>← Předchozí</button>
                <span>{total === 0 ? 0 : skip + 1}–{Math.min(skip + PAGE, total)} z {total}</span>
                <button disabled={skip + PAGE >= total} onClick={() => setSkip(skip + PAGE)}>Další →</button>
            </div>
            {confirmModal}
        </div>
    );
}
